/**
 * @module  public/js/ui/reconnectEmailBanner.js
 * @class   ReconnectEmailBanner
 * @summary Inline component that prompts the user to reconnect their email.
 *
 * @description
 * Renders a dismissible banner with a CTA that clears stored OAuth credentials
 * then relaunches the consent flow. Designed to appear when the server reports
 * `EMAIL_OAUTH_DISCONNECTED` so sellers can quickly restore access.
 */

const PROVIDER_LABELS = {
  gmail: 'Gmail',
  outlook: 'Outlook',
};

function mapProvider(value) {
  const key = String(value || '').toLowerCase();
  if (key === 'graph') return 'outlook';
  if (key === 'gmail') return 'gmail';
  if (key === 'outlook') return 'outlook';
  return 'gmail';
}

export default class ReconnectEmailBanner {
  /**
   * @param {{ apiClient: import('../core/apiclient.js').default, endpoints: import('../core/endpoints-service.js').EndpointService }} deps
   */
  constructor({ apiClient, endpoints }) {
    this.api = apiClient;
    this.endpoints = endpoints;
    this.container = null;
    this.button = null;
    this.currentProvider = 'gmail';
    this.loading = false;
  }

  #ensureContainer() {
    if (this.container) return this.container;
    const el = document.createElement('div');
    el.className = 'reconnect-email-banner';
    el.style.position = 'fixed';
    el.style.bottom = '24px';
    el.style.right = '24px';
    el.style.zIndex = '9999';
    el.style.background = '#0f172a';
    el.style.color = '#fff';
    el.style.padding = '16px 20px';
    el.style.borderRadius = '8px';
    el.style.boxShadow = '0 12px 24px rgba(15, 23, 42, 0.25)';
    el.style.maxWidth = '360px';
    el.style.fontFamily = 'Inter, system-ui, sans-serif';

    const message = document.createElement('div');
    message.className = 'reconnect-email-banner__message';
    message.style.marginBottom = '12px';
    message.style.fontSize = '14px';
    message.style.lineHeight = '1.5';
    el.appendChild(message);

    const actions = document.createElement('div');
    actions.style.display = 'flex';
    actions.style.gap = '12px';
    el.appendChild(actions);

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Reconnect';
    button.style.background = '#22d3ee';
    button.style.color = '#0f172a';
    button.style.border = 'none';
    button.style.borderRadius = '999px';
    button.style.padding = '8px 18px';
    button.style.fontWeight = '600';
    button.style.cursor = 'pointer';
    button.addEventListener('click', () => this.#handleReconnect());
    actions.appendChild(button);

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.textContent = 'Not now';
    dismiss.style.background = 'transparent';
    dismiss.style.border = 'none';
    dismiss.style.color = '#cbd5f5';
    dismiss.style.cursor = 'pointer';
    dismiss.addEventListener('click', () => this.hide());
    actions.appendChild(dismiss);

    document.body.appendChild(el);
    this.container = el;
    this.messageEl = message;
    this.button = button;
    return el;
  }

  async #handleReconnect() {
    if (this.loading) return;
    this.loading = true;
    this.button.disabled = true;
    this.button.textContent = 'Opening…';

    try {
      const backendProvider =
        this.currentProvider === 'outlook' ? 'graph' : this.currentProvider;
      await this.api.fetchJson(this.endpoints.clearEmailOAuth(), {
        auth: true,
        body: { provider: backendProvider },
      });
      const url = this.endpoints.generateEmailAuthUrl({
        provider: backendProvider,
      });
      const { oauthUrl } = await this.api.fetchJson(url, { auth: true });
      if (oauthUrl) {
        window.location.assign(oauthUrl);
        return;
      }
    } catch (err) {
      console.error('[ReconnectEmailBanner] reconnect failed', err);
      this.button.disabled = false;
      this.button.textContent = 'Reconnect';
      this.loading = false;
      return;
    }

    this.button.disabled = false;
    this.button.textContent = 'Reconnect';
    this.loading = false;
  }

  /**
   * Display the banner for the given provider.
   * @param {{ provider?: string }} [opts]
   */
  show({ provider } = {}) {
    this.#ensureContainer();
    this.currentProvider = mapProvider(provider);
    const label =
      PROVIDER_LABELS[this.currentProvider] || PROVIDER_LABELS.gmail;
    this.messageEl.textContent = `Your ${label} connection needs attention. Reconnect now to keep sending and tracking replies.`;
    this.button.textContent = `Reconnect ${label}`;
    this.button.disabled = false;
    this.loading = false;
    this.container.style.display = 'block';
  }

  /** Hide the banner. */
  hide() {
    if (!this.container) return;
    this.container.style.display = 'none';
  }
}
